import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@core/api';

interface TrolleyShelterLocationImageViewerProps {
    imagePath?: string | null;
    className?: string;
}

const TrolleyShelterLocationImageViewer: React.FC<TrolleyShelterLocationImageViewerProps> = ({
    imagePath,
    className
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [imageData, setImageData] = useState<ArrayBuffer | null>(null);
    const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        loadImage();
    }, [imagePath]);

    useEffect(() => {
        return () => {
            if (imageBlobUrl) {
                URL.revokeObjectURL(imageBlobUrl);
            }
        };
    }, [imageBlobUrl]);

    useEffect(() => {
        let cleanup: (() => void) | undefined;

        if (imageData) {
            const blob = new Blob([imageData], { type: 'image/*' });
            const url = URL.createObjectURL(blob);
            setImageBlobUrl(url);

            cleanup = () => {
                URL.revokeObjectURL(url);
            };
        } else {
            setImageBlobUrl(null);
        }

        return cleanup;
    }, [imageData]);

    const loadImage = async () => {
        if (!imagePath) {
            setImageData(null);
            setHasError(false);
            return;
        }

        setLoading(true);
        setHasError(false);
        try {
            const response = await apiClient.get<ArrayBuffer>(`trolleyshelterlocationimage?imagePathUri=${imagePath}`, {
                responseType: 'arraybuffer'
            });
            setImageData(response.data);
        } catch (error) {
            console.error('Failed to load image:', error);
            setImageData(null);
            setHasError(true);
        } finally {
            setLoading(false);
        }
    };

    if (!imagePath || loading || hasError || !imageData || !imageBlobUrl) {
        return null;
    }

    return (
        <Box sx={{ width: '100%' }} className={className}>
            <img
                src={imageBlobUrl}
                alt="Trolley shelter location"
                style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '4px'
                }}
                onError={() => {
                    console.error('Image failed to display');
                    setHasError(true);
                }}
            />
        </Box>
    );
};

export default TrolleyShelterLocationImageViewer;
